import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiConfigs } from "../../constants/apiConfigs";
import { SavedAddress } from "../../types/address";
import { getErrorMessage, isFresh } from "../../utils/helpers";
import { fetchAddress, fetchAdresses } from "../../services/api/address";

export interface AddressBook {
    loading: boolean,
    addressFetching: boolean,
    error: string|null,
    last_fetched_at: number | null,
    list: SavedAddress[],
    items: Record<string, {
        address: SavedAddress,
        last_fetched_at: number | null,
    }>
}

export const initialState: AddressBook = {
    loading: true,
    addressFetching: false,
    error: null,
    last_fetched_at: null,
    list: [],
    items: {}
}

const STALE_TIME = apiConfigs.FETCH_ADDRESSES.staleTime;

export const fetchAddressesThunk = createAsyncThunk(
    'i/addresses',
    async ({force=false}: {force?: boolean}, {getState, rejectWithValue}) => {
        const state = getState() as {addressBook: AddressBook};
        
        try {
            if(!force && isFresh(state.addressBook.last_fetched_at, STALE_TIME)){
                return {list: state.addressBook.list, cache: true}
            }
            const list = await fetchAdresses();
            return {list}
        } catch (error) {
            console.log(error)
            return rejectWithValue(getErrorMessage(error, "failed to fetch address book"));
        }

    }
)

export const fetchAddressThunk = createAsyncThunk(
    'i/address',
    async ({force=false, addressUUID}: {addressUUID: string, force?: boolean}, {getState, rejectWithValue}) => {
        const state = getState() as {addressBook: AddressBook};
        
        try {
            if(!force && isFresh(state.addressBook.items[addressUUID]?.last_fetched_at, STALE_TIME)){
                return {address: state.addressBook.items[addressUUID].address, cache: true}
            }
            const address = await fetchAddress(addressUUID);
            return {address}
        } catch (error) {
            console.log(error)
            return rejectWithValue(getErrorMessage(error, "failed to fetch address"));
        }

    }
)

const addressBook = createSlice({
    name: 'addressBook',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
        .addCase(fetchAddressesThunk.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(fetchAddressesThunk.fulfilled, (state, {payload}) => {
            const {list, cache} = payload;

            state.list = list;
            if(!cache){
                state.last_fetched_at = Date.now()
            }

            state.loading = false;
            state.error = null;

        })
        .addCase(fetchAddressesThunk.rejected, (state) => {
            state.loading = false;
            state.error = 'failed to fetch address book!'
        })
        
        .addCase(fetchAddressThunk.fulfilled, (state, {payload}) => {
            const {address, cache} = payload;
            state.items[address.uuid] = {
                ...(state.items[address.uuid] ?? {}),
                address,
                ...(!cache ? {last_fetched_at: Date.now()} : {})
            }
        })
    }
})

export default addressBook.reducer;